// Plan quotas must match DB triggers in migration 057
// (check_client_limit / check_team_member_limit). Mismatches cause the UI to
// promise slots the DB rejects, or hide slots the DB would allow.
export const PLANS = {
  trial:      { name: 'Trial',      price: 0,   maxClients: 10,   maxMembers: 1,    whatsapp: false },
  starter:    { name: 'Starter',    price: 69,  maxClients: 30,   maxMembers: 2,    whatsapp: false },
  growth:     { name: 'Growth',     price: 179, maxClients: 150,  maxMembers: 5,    whatsapp: true  },
  pro:        { name: 'Pro',        price: 349, maxClients: 500,  maxMembers: 15,   whatsapp: true  },
  enterprise: { name: 'Enterprise', price: null, maxClients: null, maxMembers: null, whatsapp: true  },
} as const

export type PlanKey = keyof typeof PLANS

/**
 * The plan whose quota actually applies. An office can sit on a paid plan while
 * its status is still 'trial' (Onboarding writes the intended plan before the
 * Stripe checkout, and an abandoned checkout leaves it there permanently), and
 * the database resolves that to the trial quota before deciding whether a write
 * fits. check_client_limit and check_team_member_limit both do it. Everything
 * that shows a limit has to agree, or the panel promises seats the DB refuses.
 */
export function effectivePlanKey(office: { plan: string; status?: string | null }): PlanKey {
  const key = office.status === 'trial' ? 'trial' : office.plan
  return (key in PLANS ? key : 'trial') as PlanKey
}

/** Self-serve upgrade ladder, cheapest first. */
const UPGRADE_PATH: readonly PlanKey[] = ['trial', 'starter', 'growth', 'pro', 'enterprise']

/**
 * The plan one step above `key`, or null at the top. Callers read its name,
 * limits and price from PLANS, so the upsell can never quote numbers that
 * disagree with the plan table (Clients kept its own copy and could drift).
 */
export function nextPlanKey(key: PlanKey): PlanKey | null {
  const i = UPGRADE_PATH.indexOf(key)
  return i >= 0 && i < UPGRADE_PATH.length - 1 ? UPGRADE_PATH[i + 1] : null
}

export const PLAN_COLORS: Record<PlanKey, string> = {
  trial:      'bg-yellow-100 text-yellow-700',
  starter:    'bg-gray-100 text-gray-700',
  growth:     'bg-blue-100 text-blue-700',
  pro:        'bg-purple-100 text-purple-700',
  enterprise: 'bg-gray-700 text-gray-100',
}

/**
 * Returns true if the office currently has WhatsApp access.
 * Rules:
 *  - Growth / Pro / Enterprise → always yes
 *  - Trial / Starter → yes only during the 30-day WhatsApp trial window
 */
export function hasWhatsappAccess(office: {
  plan: string
  whatsapp_trial_until?: string | null
}): boolean {
  const plan = PLANS[office.plan as PlanKey]
  if (plan?.whatsapp) return true
  if (!office.whatsapp_trial_until) return false
  return new Date() < new Date(office.whatsapp_trial_until)
}

/**
 * Returns the number of days remaining in the WhatsApp trial.
 * Returns null if no trial is active or already expired.
 */
export function whatsappTrialDaysLeft(office: {
  whatsapp_trial_until?: string | null
}): number | null {
  if (!office.whatsapp_trial_until) return null
  const msLeft = new Date(office.whatsapp_trial_until).getTime() - Date.now()
  if (msLeft <= 0) return null
  return Math.ceil(msLeft / (1000 * 60 * 60 * 24))
}
